import { useEffect, useRef, useState } from 'react';

/**
 * True once the element is on screen or about to be.
 *
 * For a part of a screen whose reads can wait until someone scrolls to it.
 * On a phone, the funder readiness card on Home sits below the fold and
 * opened five reads, including the whole document vault, in the same breath
 * as the work list above it. A browser without IntersectionObserver gets
 * the old behaviour: true at once.
 */
export function useNearViewport<T extends Element>(margin = '300px'): [boolean, React.RefObject<T>] {
  const ref = useRef<T>(null);
  const [near, setNear] = useState(() => typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    if (near) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin: `0px 0px ${margin} 0px` },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [near, margin]);
  return [near, ref];
}
